import type { RuntimeTileManifestV2 } from "../schema/runtimeTile.ts";
import type {
  WorldTileAddress,
  WorldTileId,
} from "../schema/tile.ts";

export const RUNTIME_TILE_LODS = {
  full: 0,
  simplified: 1,
  distant: 2,
} as const;

export type RuntimeTileLodName = keyof typeof RUNTIME_TILE_LODS;
export type RuntimeTileLod = (typeof RUNTIME_TILE_LODS)[RuntimeTileLodName];

export interface TileLodRings {
  /** Inclusive Chebyshev radius for full-detail tiles. */
  fullRadiusTiles: number;
  /** Inclusive Chebyshev radius for simplified tiles. */
  simplifiedRadiusTiles: number;
  /** Inclusive Chebyshev radius for distant tiles. */
  distantRadiusTiles: number;
}

export interface DesiredTile {
  tileId: WorldTileId;
  lod: RuntimeTileLod;
  distanceTiles: number;
}

export interface FullDetailProximity {
  readonly focus: Readonly<{ x: number; z: number }>;
  readonly radiusM: number;
}

type ManifestTile = RuntimeTileManifestV2["tiles"][number];
const manifestTileIndexes = new WeakMap<
  RuntimeTileManifestV2,
  ReadonlyMap<WorldTileId, ManifestTile>
>();

function assertRadius(value: number, name: string): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${name} must be a non-negative integer`);
  }
}

export function validateTileLodRings(rings: Readonly<TileLodRings>): void {
  assertRadius(rings.fullRadiusTiles, "fullRadiusTiles");
  assertRadius(rings.simplifiedRadiusTiles, "simplifiedRadiusTiles");
  assertRadius(rings.distantRadiusTiles, "distantRadiusTiles");
  if (
    rings.fullRadiusTiles > rings.simplifiedRadiusTiles ||
    rings.simplifiedRadiusTiles > rings.distantRadiusTiles
  ) {
    throw new Error(
      "LOD radii must be nested: full <= simplified <= distant",
    );
  }
}

function desiredLodForDistance(
  distanceTiles: number,
  rings: Readonly<TileLodRings>,
): RuntimeTileLod | undefined {
  if (distanceTiles <= rings.fullRadiusTiles) {
    return RUNTIME_TILE_LODS.full;
  }
  if (distanceTiles <= rings.simplifiedRadiusTiles) {
    return RUNTIME_TILE_LODS.simplified;
  }
  if (distanceTiles <= rings.distantRadiusTiles) {
    return RUNTIME_TILE_LODS.distant;
  }
  return undefined;
}

function chooseAvailableLod(
  desired: RuntimeTileLod,
  available: readonly number[],
): RuntimeTileLod | undefined {
  const supported = available.filter(
    (lod): lod is RuntimeTileLod => lod === 0 || lod === 1 || lod === 2,
  );
  if (supported.includes(desired)) {
    return desired;
  }
  const coarser = supported
    .filter((lod) => lod > desired)
    .sort((left, right) => left - right)[0];
  if (coarser !== undefined) {
    return coarser;
  }
  return supported.sort((left, right) => right - left)[0];
}

function tileDistance(
  left: Readonly<WorldTileAddress>,
  right: Readonly<WorldTileAddress>,
): number {
  return Math.max(Math.abs(left.x - right.x), Math.abs(left.z - right.z));
}

function distanceToBoundsM(
  point: Readonly<{ x: number; z: number }>,
  bounds: Readonly<{ minX: number; minZ: number; maxX: number; maxZ: number }>,
): number {
  const x = Math.max(bounds.minX - point.x, 0, point.x - bounds.maxX);
  const z = Math.max(bounds.minZ - point.z, 0, point.z - bounds.maxZ);
  return Math.hypot(x, z);
}

export function shouldRefreshTileSelection(
  previous: Readonly<{ x: number; z: number }> | null,
  current: Readonly<{ x: number; z: number }>,
  tileSizeM: number,
  movementThresholdM: number,
): boolean {
  if (previous === null) {
    return true;
  }
  const previousTileX = Math.floor(previous.x / tileSizeM);
  const previousTileZ = Math.floor(previous.z / tileSizeM);
  const currentTileX = Math.floor(current.x / tileSizeM);
  const currentTileZ = Math.floor(current.z / tileSizeM);
  if (previousTileX !== currentTileX || previousTileZ !== currentTileZ) {
    return true;
  }
  const deltaX = current.x - previous.x;
  const deltaZ = current.z - previous.z;
  return deltaX * deltaX + deltaZ * deltaZ >=
    movementThresholdM * movementThresholdM;
}

/**
 * Selects only manifest-backed tiles. Square/Chebyshev rings avoid gaps while
 * crossing a corner between four 256 m cells.
 */
export function selectDesiredTiles(
  manifest: Readonly<RuntimeTileManifestV2>,
  center: Readonly<WorldTileAddress>,
  rings: Readonly<TileLodRings>,
  fullDetailProximity?: Readonly<FullDetailProximity>,
): readonly DesiredTile[] {
  validateTileLodRings(rings);
  if (
    fullDetailProximity !== undefined &&
    (
      !Number.isFinite(fullDetailProximity.focus.x) ||
      !Number.isFinite(fullDetailProximity.focus.z) ||
      !Number.isFinite(fullDetailProximity.radiusM) ||
      fullDetailProximity.radiusM < 0
    )
  ) {
    throw new Error(
      "Full-detail proximity requires finite coordinates and a non-negative radius",
    );
  }
  const desired: DesiredTile[] = [];
  const tilesById = manifestTileIndex(manifest);
  const radius = rings.distantRadiusTiles;
  for (let z = center.z - radius; z <= center.z + radius; z += 1) {
    for (let x = center.x - radius; x <= center.x + radius; x += 1) {
      const entry = tilesById.get(`${x}:${z}` as WorldTileId);
      if (entry === undefined) {
        continue;
      }
      const distanceTiles = tileDistance(center, entry.address);
      const ringLod = desiredLodForDistance(distanceTiles, rings);
      if (ringLod === undefined) {
        continue;
      }
      const requestedLod =
        fullDetailProximity !== undefined &&
          distanceToBoundsM(fullDetailProximity.focus, entry.bounds) <=
            fullDetailProximity.radiusM
          ? RUNTIME_TILE_LODS.full
          : ringLod;
      const lod = chooseAvailableLod(requestedLod, entry.availableLods);
      if (lod === undefined) {
        continue;
      }
      desired.push({ tileId: entry.id, lod, distanceTiles });
    }
  }

  return desired.sort(
    (left, right) =>
      left.distanceTiles - right.distanceTiles ||
      left.lod - right.lod ||
      left.tileId.localeCompare(right.tileId),
  );
}

function manifestTileIndex(
  manifest: Readonly<RuntimeTileManifestV2>,
): ReadonlyMap<WorldTileId, ManifestTile> {
  const cached = manifestTileIndexes.get(manifest);
  if (cached !== undefined) {
    return cached;
  }
  const index = new Map<WorldTileId, ManifestTile>();
  for (const entry of manifest.tiles as readonly ManifestTile[]) {
    index.set(entry.id, entry);
  }
  manifestTileIndexes.set(manifest, index);
  return index;
}
